import { Link } from 'react-router-dom';
import { useI18n } from '../../i18n/i18n.jsx';
import { useTheme } from '../../hooks/useTheme.jsx';
import Container from '../UI/Container';

const Footer = () => {
  const { t } = useI18n();
  const { isDark } = useTheme();

  const currentYear = new Date().getFullYear();

  const quickLinks = [
    { to: '/', key: 'home' },
    { to: '/about', key: 'about' },
    { to: '/team', key: 'team' },
    { to: '/services', key: 'services' },
    { to: '/contact', key: 'contact' }
  ];

  const servicesLinks = [
    { to: '/services', key: 'universityConsulting' },
    { to: '/services', key: 'admissionSupport' },
    { to: '/services', key: 'visaConsulting' },
    { to: '/services', key: 'careerPlanning' }
  ];

  const contactInfo = [
    { icon: '💬', text: '+993 61351020', href: 'https://wa.me/99361351020' },
    { icon: '✉️', text: 'info@bdowur.com', href: 'mailto:info@bdowur.com' },
    { icon: '✉️', text: 'dovlet@bdowur.com', href: 'mailto:dovlet@bdowur.com' },
    { icon: '🌐', text: 'edu.bdowur.com', href: 'https://edu.bdowur.com', target: '_blank' },
    { icon: '📍', text: '+99312 955240', href: 'tel:+99312955240' },
    { icon: '📍', text: t('contact.direct.addressValue'), href: '#' }
  ];

  const socialLinks = [
    { 
      icon: 'wa', 
      href: 'https://wa.me/99361351020', 
      label: 'WhatsApp',
      svg: (
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
      )
    }
  ];

  return (
    <footer className={`pt-16 pb-6 md:pb-8 border-t ${isDark ? 'bg-neutral-900 border-neutral-700' : 'bg-neutral-100 border-neutral-200'}`}>
      <Container>
        {/* Main Footer Content */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-12 mb-8 md:mb-12">
          {/* Brand & Description */}
          <div className="space-y-3 md:space-y-4">
            <div className="flex items-center gap-2">
              <div className="w-6 h-6 md:w-8 md:h-8 bg-gradient-to-br from-accent to-accent-light rounded-lg flex items-center justify-center text-neutral-900 font-bold text-xs md:text-sm">
                BD
              </div>
              <span className="font-serif text-base md:text-xl font-semibold">Bereketli Dowur</span>
            </div>
            <p className="text-neutral-400 text-xs md:text-sm leading-relaxed">
              {t('hero.subtitle')}
            </p>
            {/* Social Links */}
            <div className="flex gap-2 md:gap-3 pt-2">
              {socialLinks.map((social) => (
                <a
                  key={social.icon}
                  href={social.href}
                  className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-neutral-800 hover:bg-accent hover:text-neutral-900 transition-all duration-200 flex items-center justify-center"
                  aria-label={social.label}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <svg
                    className="w-4 h-4 md:w-5 md:h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    viewBox="0 0 24 24"
                  >
                    {social.svg}
                  </svg>
                </a>
              ))}
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-4 text-accent">{t('footer.quickLinks')}</h3>
            <ul className="space-y-1.5 md:space-y-2">
              {quickLinks.map((link) => (
                <li key={link.to}>
                  <Link
                    to={link.to}
                    className="text-neutral-400 hover:text-white transition-colors duration-200 text-xs md:text-sm"
                  >
                    {t(`nav.${link.key}`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-4 text-accent">{t('services.title')}</h3>
            <ul className="space-y-1.5 md:space-y-2">
              {servicesLinks.map((link, index) => (
                <li key={index}>
                  <Link
                    to={link.to}
                    className="text-neutral-400 hover:text-white transition-colors duration-200 text-xs md:text-sm"
                  >
                    {t(`services.${link.key}.title`)}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-base md:text-lg font-semibold mb-2 md:mb-4 text-accent">{t('nav.contact')}</h3>
            <ul className="space-y-2 md:space-y-3">
              {contactInfo.map((item, index) => (
                <li key={index} className="flex items-start gap-1.5 md:gap-2 text-xs md:text-sm text-neutral-400">
                  <span className="text-base md:text-lg leading-none">{item.icon}</span>
                  {item.href === '#' ? (
                    <span>{item.text}</span>
                  ) : (
                    <a
                      href={item.href}
                      className="hover:text-white transition-colors duration-200"
                      target={item.target || '_self'}
                      rel={item.target === '_blank' ? 'noopener noreferrer' : undefined}
                    >
                      {item.text}
                    </a>
                  )}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div className={`pt-6 md:pt-8 border-t ${isDark ? 'border-neutral-700 bg-neutral-900' : 'border-neutral-200 bg-neutral-100'} flex flex-col md:flex-row justify-between items-center gap-3 md:gap-4 text-xs md:text-sm text-neutral-500`}>
          <div className="text-center md:text-left">
            © {currentYear} Bereketli Dowur. {t('footer.rights')}.
          </div>
          <div className="flex gap-4 md:gap-6 flex-wrap justify-center">
            <Link
              to="/privacy"
              className="hover:text-accent transition-colors duration-200"
            >
              {t('footer.privacy')}
            </Link>
            <Link
              to="/terms"
              className="hover:text-accent transition-colors duration-200"
            >
              {t('footer.terms')}
            </Link>
          </div>
        </div>
      </Container>
    </footer>
  );
};

export default Footer;
